import React from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  Building2,
  BriefcaseBusiness,
  BookOpen,
  Mail,
  Menu,
  Phone,
  MapPin,
} from "lucide-react";
import { brand } from "@/mock/mock";
import { cn } from "@/lib/utils";

const nav = [
  { label: "Home", to: "/" },
  { label: "Company", to: "/company" },
  { label: "Products", to: "/products" },
  { label: "Projects", to: "/projects" },
  { label: "Careers", to: "/careers" },
  { label: "Insights", to: "/insights" },
  { label: "Contact", to: "/contact" },
];

const isNavItemActive = (pathname, to) =>
  to === "/" ? pathname === "/" : pathname.startsWith(to);

const navIcon = (label) => {
  switch (label) {
    case "Company":
      return <Building2 className="h-4 w-4" />;
    case "Products":
      return <BriefcaseBusiness className="h-4 w-4" />;
    case "Careers":
      return <BriefcaseBusiness className="h-4 w-4" />;
    case "Insights":
      return <BookOpen className="h-4 w-4" />;
    case "Contact":
      return <Mail className="h-4 w-4" />;
    default:
      return null;
  }
};

const Logo = () => (
  <Link
    to="/"
    className="group inline-flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
    aria-label="Ellipsonic Home"
  >
        <img
          src="/assets/images/logo.png"
          alt="Ellipsonic logo"
          className="h-10 w-10 object-contain"
        />
    <div className="leading-tight">
      <div className="text-md font-semibold tracking-tight text-foreground">
        Ellipsonic
      </div>
    </div>
  </Link>
);

const Header = () => {
  const location = useLocation();
  const [scrolled, setScrolled] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 6);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div
      className={cn(
        "sticky top-0 z-50 border-b border-border/60 bg-background/75 backdrop-blur-[18px]",
        scrolled && "shadow-[0_10px_30px_-22px_rgba(0,0,0,0.35)]"
      )}
    >
      <div className="mx-auto max-w-6xl px-4">
        <div className="flex h-16 items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <Logo />

            <div className="hidden lg:block">
              <NavigationMenu>
                <NavigationMenuList className="gap-0">
                  {nav.map((item) => {
                    const active = isNavItemActive(location.pathname, item.to);

                    return (
                      <NavigationMenuItem key={item.to}>
                        <NavigationMenuLink asChild>
                          <NavLink
                            to={item.to}
                            end={item.to === "/"}
                            className={cn(
                              "inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground",
                              active &&
                                "bg-primary/15 font-semibold text-primary hover:bg-primary/25 hover:text-primary dark:bg-primary/20 dark:text-primary dark:hover:bg-primary/30 dark:hover:text-primary"
                            )}
                          >
                            {/* <span className="text-muted-foreground/80">
                              {navIcon(item.label)}
                            </span> */}
                            {item.label}
                          </NavLink>
                        </NavigationMenuLink>
                      </NavigationMenuItem>
                    );
                  })}
                </NavigationMenuList>
              </NavigationMenu>
            </div>
          </div>

          <div className="hidden lg:flex items-center gap-2">
            <Button asChild className="group cursor-pointer shadow-sm transition-shadow hover:shadow-md">
              <Link to="/contact" className="inline-flex items-center gap-2">
                Talk to an Expert
                <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5" aria-hidden />
              </Link>
            </Button>
            {/* <Button
              asChild
              className="group shadow-sm hover:shadow-md transition-shadow"
            >
              <Link to="/careers" className="relative">
                Careers
                <span className="pointer-events-none absolute inset-0 rounded-md bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.25),transparent_55%)] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              </Link>
            </Button> */}
          </div>

          <div className="lg:hidden flex items-center gap-2">
            <Button asChild size="sm" variant="outline" className="bg-background/60">
              <Link to="/contact">Talk</Link>
            </Button>
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button size="icon" variant="outline" className="bg-background/60">
                  <Menu className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[320px]">
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-3">
                   <img src="/assets/images/logo.png" alt="Ellipsonic logo" className="h-10 w-10 object-contain" />
                    Ellipsonic
                  </SheetTitle>
                </SheetHeader>
                <div className="mt-6 grid gap-2">
                  {nav.map((item) => {
                    const active = isNavItemActive(location.pathname, item.to);

                    return (
                      <Button
                        key={item.to}
                        asChild
                        variant={active ? "default" : "outline"}
                        className="justify-start"
                      >
                        <Link
                          to={item.to}
                          onClick={() => setMobileOpen(false)}
                        >
                          <span className="mr-2">{navIcon(item.label)}</span>
                          {item.label}
                        </Link>
                      </Button>
                    );
                  })}
                </div>
                <Separator className="my-6" />
                <div className="text-xs text-muted-foreground">
                  <div className="font-medium text-foreground">Contact</div>
                  <div className="mt-2 flex items-start gap-2">
                    <MapPin className="h-4 w-4 mt-0.5" />
                    <span>{brand.contact.address}</span>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    <span>{brand.contact.phone}</span>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </div>
  );
};

const Footer = () => (
  <footer className="border-t border-border/70 bg-background">
    <div className="mx-auto max-w-6xl px-4 py-10">
      <div className="grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Logo />
          <p className="mt-4 text-sm text-muted-foreground max-w-sm">
            {brand.subtagline}
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <Badge variant="secondary" className="bg-secondary/60">AI + Data</Badge>
            <Badge variant="secondary" className="bg-secondary/60">Cloud Engineering</Badge>
            <Badge variant="secondary" className="bg-secondary/60">Product Delivery</Badge>
          </div>
        </div>

        <div className="lg:col-span-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="text-sm font-semibold">About</div>
            <div className="mt-3 grid gap-2 text-sm">
              <Link className="text-muted-foreground hover:text-foreground transition-colors" to="/company">
                Company
              </Link>
              <Link className="text-muted-foreground hover:text-foreground transition-colors" to="/company#leadership">
                Leadership
              </Link>
              <Link className="text-muted-foreground hover:text-foreground transition-colors" to="/company#engagement">
                Engagement Models
              </Link>
              <Link className="text-muted-foreground hover:text-foreground transition-colors" to="/company#compliance">
                Legal & Compliance
              </Link>
            </div>
          </div>

          <div>
            <div className="text-sm font-semibold">Explore</div>
            <div className="mt-3 grid gap-2 text-sm">
              <Link className="text-muted-foreground hover:text-foreground transition-colors" to="/products">
                Products
              </Link>
              <Link className="text-muted-foreground hover:text-foreground transition-colors" to="/projects">
                Projects
              </Link>
              <Link className="text-muted-foreground hover:text-foreground transition-colors" to="/insights">
                Insights
              </Link>
              <Link className="text-muted-foreground hover:text-foreground transition-colors" to="/contact">
                Contact
              </Link>
            </div>
          </div>

          <div>
            <div className="text-sm font-semibold">Careers</div>
            <div className="mt-3 grid gap-2 text-sm">
              <Link className="text-muted-foreground hover:text-foreground transition-colors" to="/careers">
                30-Hour Model
              </Link>
              <Link className="text-muted-foreground hover:text-foreground transition-colors" to="/careers">
                Work Culture
              </Link>
              <Link className="text-muted-foreground hover:text-foreground transition-colors" to="/contact">
                Hiring Enquiry
              </Link>
            </div>
          </div>

          <div>
            <div className="text-sm font-semibold">Contact</div>
            <div className="mt-3 grid gap-3 text-sm text-muted-foreground">
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <span>{brand.contact.address}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4" />
                <a
                  className="hover:text-foreground transition-colors"
                  href={`tel:${brand.contact.phone.replace(/\s/g, "")}`}
                >
                  {brand.contact.phone}
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                <a className="hover:text-foreground transition-colors" href={`mailto:${brand.contact.emails[0]}`}>
                  {brand.contact.emails[0]}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Separator className="my-8" />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-xs text-muted-foreground">
          © {new Date().getFullYear()} {brand.name}. All rights reserved.
        </div>
        <div className="flex flex-wrap gap-3 text-xs">
          <a className="text-muted-foreground hover:text-foreground transition-colors" href="#">
            Privacy Policy
          </a>
          <a className="text-muted-foreground hover:text-foreground transition-colors" href="#">
            Terms
          </a>
          <a className="text-muted-foreground hover:text-foreground transition-colors" href="#">
            NDA
          </a>
        </div>
      </div>
    </div>
  </footer>
);

export const SiteLayout = () => {
  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <Header />
      <main className="relative">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.65]"
        >
          <div className="absolute -top-24 left-1/2 h-[520px] w-[900px] -translate-x-1/2 rounded-full bg-[radial-gradient(circle_at_30%_30%,rgba(255,145,56,0.22),transparent_60%)] blur-3xl" />
          <div className="absolute -top-10 right-[-180px] h-[460px] w-[460px] rounded-full bg-[radial-gradient(circle_at_30%_30%,rgba(255,145,56,0.18),transparent_62%)] blur-3xl" />
        </div>
        <div className="relative">
          <Outlet />
        </div>
      </main>
      <Footer />
    </div>
  );
};
